import { useEffect } from 'react';
import { useListContext, useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import CanIcon from '@/assets/icons/can-icon.svg?react';
import BottleIcon from '@/assets/icons/bottle-icon.svg?react';
import RefreshIcon from '@/assets/icons/refresh-icon.svg?react';
import ActiveIcon from '@/assets/icons/active-icon.svg?react';
import DisactiveIcon from '@/assets/icons/disactive-icon.svg?react';
import { StatisticCard, MultiStatisticCard, StatisticCardWithGrowth, StatisticCardWithBarIndicator } from './statisticCard.jsx';
import { TagRanking } from '@/components/analytics/TagRanking.jsx';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';
import { formatDateToTimeString } from '@/helpers/date/formatDateToTimeString.js';

const StyledStackBoxes = styled(Stack)(({ theme }) => ({
    '&.statistics-boxes': {
        display: 'flex',
        gap: '20px',
        margin: '0px 0 40px 0',
        flexWrap: 'wrap',
        [theme.breakpoints.down('md')]: {
            marginTop: '20px',
            marginBottom: '20px !important',
        },
        '& .MuiPaper-root.statistic-card': {
            margin: 0,
            [theme.breakpoints.down('lg')]: {
                minWidth: '45%',
                width: '50%',
            },
            [theme.breakpoints.down('md')]: {
                minWidth: '100%',
                width: '100%',
            },
            '& a, & a:hover, & a:active, & a:focus': {
                textDecoration: 'none !important',
                color: Palette.black,
            },
            '& a:hover': {
                boxShadow: Palette.boxShadow.elevation3,
            },
            '& .MuiCircularProgress-root': {
                width: '30px !important',
                height: '30px !important',
                transform: 'none !important',
            },
            '& > .MuiBox-root, & > a': {
                backgroundColor: Palette.white,
                borderRadius: '16px',
                padding: '32px',
                display: 'block',
                [theme.breakpoints.down('md')]: {
                    padding: '16px 20px',
                },
                '& .statistic-card-with-growth': {
                    display: 'flex',
                    alignItems: 'center',
                },
                '& .statistic-card-status': {
                    marginLeft: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    lineHeight: '20px',
                    whiteSpace: 'nowrap',
                    '&.statistic-card-status--current': {
                        color: Palette.green.green700,
                    },
                    '&.statistic-card-status--outdated': {
                        color: Palette.red.red600,
                    },
                },
                '& .MuiTypography-root': {
                    '&.MuiTypography-body1': {
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '20px',
                        letterSpacing: '-0.14px',
                        marginBottom: '4px',
                        minHeight: 'auto',
                        height: 'auto',
                        color: Palette.black,
                    },
                    '&.MuiTypography-h3': {
                        lineHeight: '36px',
                        letterSpacing: '-1.02px',
                        display: 'flex',
                        alignItems: 'center',
                        color: Palette.black,
                        '& + .MuiChip-root': {
                            marginLeft: '12px',
                            padding: '4px 8px',
                            '& .MuiChip-label': {
                                lineHeight: '20px',
                                padding: '0',
                            },
                        },
                        '& svg': {
                            width: 'auto',
                            height: '20px',
                            marginLeft: '8px',
                            position: 'relative',
                            top: '-1px',
                        },
                    },
                },
                '& .multi-box': {
                    display: 'flex',
                    flexWrap: 'no-wrap',
                },
            },
            '&.statistic-card--multiple': {
                '& > .MuiBox-root': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '32px',
                },
                '& > a > .MuiBox-root': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '32px',
                    [theme.breakpoints.down('lg')]: {
                        display: 'block',
                    },
                },
                '& a:hover': {
                    boxShadow: Palette.boxShadow.elevation3,
                },
                '& .main-info': {
                    [theme.breakpoints.down('lg')]: {
                        marginBottom: '12px',
                    },
                    '& .MuiTypography-root.MuiTypography-body1': {
                        marginBottom: '0',
                        whiteSpace: 'nowrap',
                    },
                    '& .MuiTypography-root.MuiTypography-h3': {
                        lineHeight: '36px',
                        letterSpacing: '-1.02px',
                    },
                },
                '& .details-info': {
                    display: 'flex',
                    alignItems: 'center',
                    border: `2px solid ${Palette.gray.gray100}`,
                    borderRadius: '8px',
                    margin: '-2px 0 -4px 0',
                    '& .item': {
                        padding: '12px 16px',
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        '& + .item': {
                            borderLeft: `2px solid ${Palette.gray.gray100}`,
                        },
                        '& .icon': {
                            width: '24px',
                            height: '24px',
                        },
                        '& .content': {
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0px',
                            '& .MuiTypography-root.MuiTypography-body1': {
                                marginBottom: '0px',
                                fontSize: '11px',
                                lineHeight: '16px',
                                fontWeight: '400',
                                letterSpacing: '-0.11px',
                                whiteSpace: 'nowrap',
                            },
                            '& .MuiTypography-root.MuiTypography-h4': {
                                fontSize: '16px',
                                lineHeight: '22px',
                                fontWeight: '600',
                                letterSpacing: '-0.16px',
                            },
                        },
                    },
                },
            },
        },
    },
}));

const calculateAveragePackageDuration = (duration, packages) => {
    const d = Number(duration);
    const p = Number(packages);
    if (!Number.isFinite(d) || !Number.isFinite(p) || p <= 0) {
        return 0;
    }
    return d / p;
};

export const TransactionStatisticList = () => {
    const dataProvider = useDataProvider();
    const { filterValues, filter } = useListContext();
    const notify = useNotify();
    const translate = useTranslate();

    const { data, isFetching, error } = useQuery({
        queryKey: ['transaction', 'summary', { filter: { ...filterValues, ...filter } }],
        queryFn: () => dataProvider.summary('transaction/summary', { filter: { ...filterValues, ...filter } }),
    });

    if (error) {
        notify(error.message, { type: 'error' });
        return null;
    }

    const {
        totalNumberOfTransactions,
        totalNumberOfPackages,
        averageTransactionDuration,
        averageNumberOfPackages,
        previousTotalNumberOfTransactions,
        previousTotalNumberOfPackages,
        previousAverageTransactionDuration,
        previousAverageNumberOfPackages,
    } = data?.data ?? {};

    const averagePackageDuration = calculateAveragePackageDuration(averageTransactionDuration, averageNumberOfPackages);
    const previousAveragePackageDuration = calculateAveragePackageDuration(previousAverageTransactionDuration, previousAverageNumberOfPackages);

    return (
        <StyledStackBoxes className='statistics-boxes' spacing={2} direction='row' m={4}>
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.totalTransactions')}
                isFetching={isFetching}
                current={totalNumberOfTransactions ?? 0}
                previous={previousTotalNumberOfTransactions ?? 0}
                link='/analytic-transactions'
            />
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.totalPackages')}
                isFetching={isFetching}
                current={totalNumberOfPackages ?? 0}
                previous={previousTotalNumberOfPackages ?? 0}
                link='/analytic-transactions'
            />
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.averagePackages')}
                isFetching={isFetching}
                current={averageNumberOfPackages ?? 0}
                previous={previousAverageNumberOfPackages ?? 0}
                digits={2}
                link='/analytic-transactions'
            />
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.averageTransactionDuration')}
                isFetching={isFetching}
                current={averageTransactionDuration ?? 0}
                previous={previousAverageTransactionDuration ?? 0}
                format='time'
                reversed={true}
                link='/analytic-transactions'
            />
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.averagePackageDuration')}
                isFetching={isFetching}
                current={averagePackageDuration}
                previous={previousAveragePackageDuration}
                format='time'
                reversed={true}
                link='/analytic-transactions'
            />
        </StyledStackBoxes>
    );
};

export const ProductsStatisticList = () => {
    const dataProvider = useDataProvider();
    const { filterValues } = useListContext();
    const notify = useNotify();
    const translate = useTranslate();

    const { data, isFetching, error } = useQuery({
        queryKey: ['product', 'summary', { filter: { ...filterValues } }],
        queryFn: () => dataProvider.summary('product/summary', { filter: { ...filterValues } }),
    });

    if (error) {
        notify(error.message, { type: 'error' });
        return null;
    }

    const { productsCount, packagesCount, petPackagesCount, metalPackagesCount } = data?.data ?? {};

    const loader = <CircularProgress />;

    return (
        <StyledStackBoxes className='statistics-boxes' spacing={2} direction='row' m={4}>
            <StatisticCard title={translate('dashboard.statistics.productsCount')} value={isFetching ? loader : productsCount} link='/analytic-products' />
            <MultiStatisticCard
                className='multi-box'
                mainTitle={translate('dashboard.statistics.totalCollected')}
                mainValue={isFetching ? loader : packagesCount}
                firstIcon={<BottleIcon />}
                firstTitle={translate('dashboard.statistics.plasticBottles')}
                firstValue={isFetching ? loader : petPackagesCount}
                secondIcon={<CanIcon />}
                secondTitle={translate('dashboard.statistics.cans')}
                secondValue={isFetching ? loader : metalPackagesCount}
                link='/analytic-products'
            />
            <TagRanking productPage={true} />
        </StyledStackBoxes>
    );
};

export const DeviceStatisticList = () => {
    const dataProvider = useDataProvider();
    const { filterValues, filter } = useListContext();
    const notify = useNotify();
    const translate = useTranslate();

    const { data, isFetching, error } = useQuery({
        queryKey: ['device', 'summary', { filter: { ...filterValues, ...filter } }],
        queryFn: () => dataProvider.summary('device/summary', { filter: { ...filterValues, ...filter } }),
    });

    if (error) {
        notify(error.message, { type: 'error' });
        return null;
    }

    const { devicesCount, transactionsCount, previousTransactionsCount, bagsCount, previousBagsCount, uptime, previousUptime, serviceRequestCount } =
        data?.data ?? {};

    return (
        <StyledStackBoxes className='statistics-boxes' spacing={2} direction='row' m={4}>
            <StatisticCard title={translate('dashboard.statistics.devicesCount')} isFetching={isFetching} value={devicesCount} />
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.totalTransactionsCount')}
                isFetching={isFetching}
                current={transactionsCount ?? 0}
                previous={previousTransactionsCount ?? 0}
                link='/analytic-transactions'
            />
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.totalCollectedBags')}
                isFetching={isFetching}
                current={bagsCount ?? 0}
                previous={previousBagsCount ?? 0}
                link='/analytic-transactions'
            />
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.averageUptime')}
                isFetching={isFetching}
                current={uptime ?? 0}
                previous={previousUptime ?? 0}
                format='percent'
                link='/analytic-service'
            />
            <StatisticCard
                title={translate('dashboard.statistics.problems')}
                isFetching={isFetching}
                value={serviceRequestCount}
                icon={true}
                link='/service-request'
            />
        </StyledStackBoxes>
    );
};

export const CollectionPointStatisticList = () => {
    const dataProvider = useDataProvider();
    const { filterValues, filter } = useListContext();
    const notify = useNotify();
    const translate = useTranslate();

    const { data, isFetching, error } = useQuery({
        queryKey: ['collection-point', 'summary', { filter: { ...filterValues, ...filter } }],
        queryFn: () => dataProvider.summary('collection-point/summary', { filter: { ...filterValues, ...filter } }),
    });

    if (error) {
        notify(error.message, { type: 'error' });
        return null;
    }

    const { devicesCount, transactionsCount, previousTransactionsCount, bagsCount, previousBagsCount, uptime, previousUptime } = data?.data ?? {};

    return (
        <StyledStackBoxes className='statistics-boxes' spacing={2} direction='row' m={4}>
            <StatisticCard title={translate('dashboard.statistics.devicesCount')} isFetching={isFetching} value={devicesCount} />
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.totalTransactionsCount')}
                isFetching={isFetching}
                current={transactionsCount ?? 0}
                previous={previousTransactionsCount ?? 0}
                link='/analytic-transactions'
            />
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.totalCollectedBags')}
                isFetching={isFetching}
                current={bagsCount ?? 0}
                previous={previousBagsCount ?? 0}
                link='/analytic-transactions'
            />
            <StatisticCardWithGrowth
                title={translate('dashboard.statistics.averageUptime')}
                isFetching={isFetching}
                current={uptime ?? 0}
                previous={previousUptime ?? 0}
                format='percent'
                link='/analytic-service'
            />
        </StyledStackBoxes>
    );
};

export const AccessKeyStatisticList = () => {
    const dataProvider = useDataProvider();
    const { filterValues, filter } = useListContext();
    const notify = useNotify();
    const translate = useTranslate();

    const { data, isFetching, error } = useQuery({
        queryKey: ['access-key', 'summary', { filter: { ...filterValues, ...filter } }],
        queryFn: () => dataProvider.summary('access-key/summary', { filter: { ...filterValues, ...filter } }),
    });

    if (error) {
        notify(error.message, { type: 'error' });
        return null;
    }

    const { devicesWithoutKeyCount, activeKeysCount, rotationKeysCount, revokedKeysCount, last24hKeysCount, last7dKeysCount, older7dKeysCount } =
        data?.data ?? {};

    return (
        <StyledStackBoxes className='statistics-boxes' spacing={2} direction='row' m={4}>
            <StatisticCard title={translate('dashboard.statistics.devicesWithoutKeyCount')} isFetching={isFetching} value={devicesWithoutKeyCount} />
            <MultiStatisticCard
                className='multi-box'
                mainTitle={translate('dashboard.statistics.allKeysCount')}
                mainValue={isFetching ? <CircularProgress size={24} /> : (activeKeysCount ?? 0) + (rotationKeysCount ?? 0) + (revokedKeysCount ?? 0)}
                firstIcon={<ActiveIcon />}
                firstTitle={translate('dashboard.statistics.activeKeysCount')}
                firstValue={isFetching ? <CircularProgress size={24} /> : activeKeysCount}
                secondIcon={<RefreshIcon />}
                secondTitle={translate('dashboard.statistics.rotationKeysCount')}
                secondValue={isFetching ? <CircularProgress size={24} /> : rotationKeysCount}
                thirdIcon={<DisactiveIcon />}
                thirdTitle={translate('dashboard.statistics.revokedKeysCount')}
                thirdValue={isFetching ? <CircularProgress size={24} /> : revokedKeysCount}
            />
            <StatisticCard title={translate('dashboard.statistics.last24hKeysCount')} isFetching={isFetching} value={last24hKeysCount} />
            <StatisticCard title={translate('dashboard.statistics.last7dKeysCount')} isFetching={isFetching} value={last7dKeysCount} />
            <StatisticCard title={translate('dashboard.statistics.older7dKeysCount')} isFetching={isFetching} value={older7dKeysCount} />
        </StyledStackBoxes>
    );
};

export const PromotionalCampaignsStatisticList = () => {
    // FOR TESTING. DELETE WHILE IMPLEMENTING REAL DATA
    const mockData = {
        isFetching: false,
        views: 4308,
        previousViews: 3010,
        conversion: 67.4,
    };

    const { isFetching, views, previousViews, conversion } = mockData;
    const translate = useTranslate();

    return (
        <StyledStackBoxes className='statistics-boxes' spacing={2} direction='row' m={4}>
            <StatisticCardWithBarIndicator title={translate('dashboard.statistics.averageConversion')} isFetching={isFetching} value={conversion} />
            <StatisticCardWithGrowth title={translate('dashboard.statistics.campaignViews')} isFetching={isFetching} current={views} previous={previousViews} />
        </StyledStackBoxes>
    );
};

const ParameterSummaryStatus = ({ isUpToDate }) => {
    const translate = useTranslate();

    return (
        <Typography variant='body2' className={`statistic-card-status ${isUpToDate ? 'statistic-card-status--current' : 'statistic-card-status--outdated'}`}>
            {translate(isUpToDate ? 'resources.parameter.statistics.upToDate' : 'resources.parameter.statistics.outdated')}
        </Typography>
    );
};

ParameterSummaryStatus.propTypes = {
    isUpToDate: PropTypes.bool.isRequired,
};

export const ParameterStatisticList = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();

    const { data, isPending, error } = useQuery({
        queryKey: ['parameter', 'summary'],
        queryFn: () => dataProvider.getRaw('/parameter/summary'),
    });

    useEffect(() => {
        if (error) {
            notify(error.message, { type: 'error' });
        }
    }, [error, notify]);

    if (error) {
        return null;
    }

    const summary = data?.data;
    const lastGeneratedTime = summary?.lastGeneratedAt ? formatDateToTimeString(new Date(summary.lastGeneratedAt)) : '';

    return (
        <StyledStackBoxes className='statistics-boxes' spacing={2} direction='row' m={4}>
            <StatisticCard
                title={translate('resources.parameter.statistics.total')}
                tooltip={translate('resources.parameter.statistics.totalTooltip')}
                isFetching={isPending}
                value={summary?.totalCount ?? 0}
            />
            <StatisticCard
                title={translate('resources.parameter.statistics.significant')}
                tooltip={translate('resources.parameter.statistics.significantTooltip')}
                isFetching={isPending}
                value={summary?.significantCount ?? 0}
            />
            <StatisticCard
                title={translate('resources.parameter.statistics.allDevices')}
                tooltip={translate('resources.parameter.statistics.allDevicesTooltip')}
                isFetching={isPending}
                value={summary?.allDevicesCount ?? 0}
            />
            <StatisticCard
                title={translate('resources.parameter.statistics.lastGeneration')}
                tooltip={translate('resources.parameter.statistics.lastGenerationTooltip')}
                isFetching={isPending}
                value={lastGeneratedTime || '—'}
                status={summary ? <ParameterSummaryStatus isUpToDate={summary.isUpToDate} /> : null}
            />
        </StyledStackBoxes>
    );
};

export const DeviceModelStatisticList = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();

    const { data, isPending, error } = useQuery({
        queryKey: ['device-model', 'summary'],
        queryFn: () => dataProvider.getRaw('/device-model/summary'),
    });

    useEffect(() => {
        if (error) {
            notify(error.message, { type: 'error' });
        }
    }, [error, notify]);

    if (error) {
        return null;
    }

    const summary = data?.data;
    const modelName = summary?.mostPopular?.name ?? '';
    const isModelNameTruncated = modelName.length > 24;
    let modelNameLabel = modelName;

    if (!modelName) {
        modelNameLabel = '—';
    } else if (isModelNameTruncated) {
        modelNameLabel = `${modelName.slice(0, 24).trimEnd()}...`;
    }

    return (
        <StyledStackBoxes className='statistics-boxes' spacing={2} direction='row' m={4}>
            <StatisticCard
                title={translate('resources.deviceModel.statistics.total')}
                tooltip={translate('resources.deviceModel.statistics.totalTooltip')}
                isFetching={isPending}
                value={summary?.totalCount ?? 0}
            />
            <StatisticCard
                title={translate('resources.deviceModel.statistics.mostPopular')}
                tooltip={isModelNameTruncated ? modelName : undefined}
                isFetching={isPending}
                value={modelNameLabel}
            />
        </StyledStackBoxes>
    );
};

DeviceModelStatisticList.propTypes = {};

export default TransactionStatisticList;
